
import { Link } from "react-router-dom";
import { Logo } from "@/components/ui/logo";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SidebarNavItems } from "./SidebarNavItems";
import { SidebarCoursesList } from "./SidebarCoursesList";
import { SidebarUserProfile } from "./SidebarUserProfile";
import { useEffect, memo, useRef, ReactNode } from "react";
import { devLog } from "@/utils/devLog";

interface StudentSidebarProps {
  notifications?: number;
  children?: ReactNode; // Added this prop to accept children
}

// Using React.memo with comparison function to prevent unnecessary renders
const StudentSidebar = memo(function StudentSidebar({ notifications = 0, children }: StudentSidebarProps) {
  const renderCountRef = useRef(0);
  const instanceIdRef = useRef(`sidebar-${Math.random().toString(36).substring(2, 8)}`);
  
  // Only log on mount/unmount to avoid console spam
  useEffect(() => {
    renderCountRef.current += 1;
    devLog("[StudentSidebar] Component mounted", { 
      instanceId: instanceIdRef.current,
      renderCount: renderCountRef.current,
      timestamp: new Date().toISOString()
    });
    
    return () => {
      devLog("[StudentSidebar] Component unmounting", {
        instanceId: instanceIdRef.current,
        finalRenderCount: renderCountRef.current
      });
    };
  }, []); // Empty dependency array means this only runs on mount/unmount
  
  return (
    <div 
      className="w-[280px] border-r bg-background fixed h-screen flex flex-col z-20 sidebar-container"
    >
      <div className="p-6 flex flex-col h-full">
        <div className="mb-10">
          <Link 
            to="/"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Logo />
          </Link>
        </div>
        
        <ScrollArea className="flex-1 h-[calc(100vh-240px)]">
          <div className="space-y-4">
            <SidebarNavItems />
            <SidebarCoursesList />
            {children} {/* Render the children here */}
          </div>
        </ScrollArea>
      
        <SidebarUserProfile notifications={notifications} />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Return true if notifications count hasn't changed (to prevent re-renders)
  return prevProps.notifications === nextProps.notifications;
});

export { StudentSidebar };
